// PageIntro — компактная плашка-подсказка под шапкой страницы.
// По умолчанию свёрнута (только заголовок и иконка info). Пользователь
// может развернуть кликом по заголовку — состояние «развёрнуто» сохраняется
// в localStorage по уникальному ключу. Свернёт обратно — запись стирается.
//
// Использование:
//   <PageIntro
//       storageKey="students"
//       title="О странице «Ученики»"
//       text="Здесь хранятся все ученики вашего центра…"
//       bullets={[
//           'Кликните на строку — откроется карточка',
//           'Кнопка «+» — добавить нового ученика',
//       ]}
//   />

import React, { useState, useCallback } from 'react';
import cl from './PageIntro.module.css';
import { IconInfo, IconCheck, IconChevronDown } from '../Icons';

const STORAGE_PREFIX = 'sk-intro-expanded:';

function readExpanded(storageKey) {
    if (!storageKey) return false;
    try {
        return localStorage.getItem(STORAGE_PREFIX + storageKey) === '1';
    } catch {
        return false;
    }
}

function writeExpanded(storageKey, expanded) {
    if (!storageKey) return;
    try {
        if (expanded) {
            localStorage.setItem(STORAGE_PREFIX + storageKey, '1');
        } else {
            localStorage.removeItem(STORAGE_PREFIX + storageKey);
        }
    } catch {
        // ignore
    }
}

const PageIntro = ({ storageKey, title, text, bullets }) => {
    // По умолчанию — свёрнуто. Если в localStorage прежде явно развернули — раскрываем.
    const [collapsed, setCollapsed] = useState(() => !readExpanded(storageKey));

    const toggle = useCallback(() => {
        setCollapsed(prev => {
            const next = !prev;
            // expanded = !collapsed → пишем true, когда РАСКРЫЛИ
            writeExpanded(storageKey, !next);
            return next;
        });
    }, [storageKey]);

    return (
        <div className={`${cl.intro} ${collapsed ? cl.collapsed : ''}`}>
            <button
                type="button"
                className={cl.head}
                onClick={toggle}
                aria-expanded={!collapsed}
                aria-label={collapsed ? 'Развернуть описание' : 'Свернуть описание'}
            >
                <span className={cl.icon}>
                    <IconInfo width={18} height={18}/>
                </span>
                <span className={cl.title}>{title}</span>
                <span className={`${cl.chevron} ${collapsed ? cl.chevronCollapsed : ''}`}>
                    <IconChevronDown width={16} height={16}/>
                </span>
            </button>

            {!collapsed && (
                <div className={cl.body}>
                    {text && <p className={cl.text}>{text}</p>}
                    {bullets && bullets.length > 0 && (
                        <ul className={cl.bullets}>
                            {bullets.map((b, i) => (
                                <li key={i} className={cl.bulletItem}>
                                    <span className={cl.bulletIcon}>
                                        <IconCheck width={11} height={11}/>
                                    </span>
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
};

export default PageIntro;
